import clsx from 'clsx';
import { useDataGridLightSettings } from '../../hooks';
import { DataGridLightColumn } from '../../models';
import React, { memo, useMemo } from 'react';
import style from './index.module.scss';
import { DataGridEditorContainer } from '../data-grid-editor-container';

interface Props<T = any> {
  row: T;
  column: DataGridLightColumn<any, any>;
  /** `td` in the table (default); `div` when the card list lays the same cell out (Phase F1). */
  component?: 'td' | 'div';
}

export const DataGridLightCell = memo<Props>(({ column, row, component = 'td' }) => {
  const { classes } = useDataGridLightSettings();

  const props = useMemo(() => {
    if (typeof column.tdProps === 'function') {
      return column.tdProps(row);
    }
    return column.tdProps;
  }, [column, row]);

  const isEditable = useMemo(() => {
    if (typeof column.editable === 'function') {
      return column.editable({ row });
    }
    return !!column.editable;
  }, [column, row]);

  if (isEditable && !column.Editor) {
    throw new Error('client-error: Editor is required');
  }

  const columnContent = column.renderColumn({ field: column.field, row });

  const Cell = component;
  return (
    <Cell
      {...(props as React.HTMLAttributes<HTMLElement>)}
      className={clsx(
        style.cell,
        classes.cell,
        props?.className,
        {
          [style.clickable]: props?.onClick,
          // Sticky columns are a table notion; on a card the cell is a plain div.
          [style.sticky]: component === 'td' && (column.stickyLeft || column.stickyRight),
          [style.stickyLeft]: component === 'td' && column.stickyLeft,
          [style.stickyRight]: component === 'td' && column.stickyRight,
        },
        style.td,
      )}
    >
      {isEditable && column.Editor ? (
        <DataGridEditorContainer row={row} field={column.field} Editor={column.Editor}>
          {columnContent}
        </DataGridEditorContainer>
      ) : (
        columnContent
      )}
    </Cell>
  );
});
